import { useEffect } from 'react'
import $api from '../../../api/api'

export interface SiteTheme {
    bgBase?: string | null
    bgSurface?: string | null
    bgMuted?: string | null
    bgTint?: string | null
    bgDark?: string | null
    textPrimary?: string | null
    textSecondary?: string | null
    textTertiary?: string | null
    accentPrimary?: string | null
    accentPrimaryHover?: string | null
    accentSuccess?: string | null
    fontSizeBody?: number | null
    fontSizeH1?: number | null
    fontSizeH2?: number | null
}

const CSS_VAR_MAP: Record<keyof Omit<SiteTheme, 'fontSizeBody' | 'fontSizeH1' | 'fontSizeH2'>, string> = {
    bgBase: '--bg-base',
    bgSurface: '--bg-surface',
    bgMuted: '--bg-muted',
    bgTint: '--bg-tint',
    bgDark: '--bg-dark',
    textPrimary: '--text-primary',
    textSecondary: '--text-secondary',
    textTertiary: '--text-tertiary',
    accentPrimary: '--accent-primary',
    accentPrimaryHover: '--accent-primary-hover',
    accentSuccess: '--accent-success',
}

// CSS variables are declared on .app_light_theme / .app_dark_theme (the .app div),
// not on :root. Inline styles on that same element win over class-based declarations,
// so we target .app directly instead of document.documentElement.
function getTargetEl(): HTMLElement {
    return (document.querySelector('.app') as HTMLElement | null) ?? document.documentElement
}

export function applySiteTheme(theme: SiteTheme) {
    const el = getTargetEl()

    for (const [key, cssVar] of Object.entries(CSS_VAR_MAP)) {
        const value = theme[key as keyof typeof CSS_VAR_MAP]
        if (value) {
            el.style.setProperty(cssVar, value)
        } else {
            el.style.removeProperty(cssVar)
        }
    }

    if (theme.fontSizeBody) {
        el.style.setProperty('--font-size-body', `${theme.fontSizeBody}px`)
    } else {
        el.style.removeProperty('--font-size-body')
    }

    if (theme.fontSizeH1) {
        el.style.setProperty('--font-size-h1', `${theme.fontSizeH1}px`)
    } else {
        el.style.removeProperty('--font-size-h1')
    }

    if (theme.fontSizeH2) {
        el.style.setProperty('--font-size-h2', `${theme.fontSizeH2}px`)
    } else {
        el.style.removeProperty('--font-size-h2')
    }
}

export const useSiteTheme = () => {
    useEffect(() => {
        $api.get<SiteTheme>('/site-theme').then((res) => {
            applySiteTheme(res.data)
        }).catch(() => {})
    }, [])
}
